import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, Check, ShieldCheck, Star } from "lucide-react";
import EstimateForm from "@/components/EstimateForm";

const Hero = ({ overline, lines, sub, bullets, image, imageAlt, formTitle, formSource, idPrefix }) => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);

  return (
    <section ref={ref} className="relative overflow-hidden bg-navy" data-testid="hero-section">
      <motion.div style={{ y }} className="absolute inset-0" aria-hidden="true">
        <img src={image} alt={imageAlt} className="h-[118%] w-full object-cover opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/55" />
      </motion.div>

      <div className="relative mx-auto grid max-w-7xl items-center gap-12 px-6 py-16 md:py-24 lg:grid-cols-[1.15fr_1fr] lg:px-10">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-salmon-vibrant"
          >
            {overline}
          </motion.p>

          <h1 className="mt-4 font-serif text-4xl font-bold leading-[1.1] tracking-tight text-white sm:text-5xl lg:text-6xl">
            {lines.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.15 + i * 0.13, ease: [0.22, 1, 0.36, 1] }}
                >
                  {line.accent ? <em className="italic text-salmon-vibrant">{line.text}</em> : line.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.55 }}
            className="mt-5 max-w-xl text-base leading-relaxed text-white/75 md:text-lg"
          >
            {sub}
          </motion.p>

          <motion.ul
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mt-6 grid gap-2.5 sm:grid-cols-2"
          >
            {bullets.map((b) => (
              <li key={b} className="flex items-center gap-2.5 text-sm font-medium text-white/90">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-salmon">
                  <Check className="h-3 w-3 text-white" strokeWidth={3.5} />
                </span>
                {b}
              </li>
            ))}
          </motion.ul>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.85 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-3 border-t border-white/15 pt-6"
            data-testid="trust-badges"
          >
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
              <ShieldCheck className="h-4 w-4 text-salmon-vibrant" /> Licensed & Insured
            </span>
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
              <Star className="h-4 w-4 fill-amber-400 text-amber-400" /> 5.0 Google Rating
            </span>
            <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-white/80">
              <BadgeCheck className="h-4 w-4 text-salmon-vibrant" /> Free Estimates
            </span>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
        >
          <EstimateForm
            idPrefix={idPrefix}
            title={formTitle}
            subtitle="Tell us about your project — we'll call you back fast."
            source={formSource}
          />
        </motion.div>
      </div>
    </section>
  );
};

export default Hero;
